/**
 * Declarative list watch query for agents / console.
 *
 * Compiles to a pure map JS body (+ optional length condition) so the host
 * pipeline stays: output → map → hash/condition → lane.
 *
 * Thin list (no threshold) — wake when id/battery shape changes:
 *   { items: "drones", select: ["drone_id", "battery"], when: "changed" }
 *
 * Filtered list — only matching items:
 *   { items: "drones", where: { path: "battery", op: "lt", value: 30 },
 *     select: ["drone_id", "battery"], when: "changed" }
 */
import { z } from 'zod';
import { parseWatchSelector } from './watch-selector.js';
const opSchema = z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq']);
/** Dot path relative to each array element (or to root when items is omitted). */
const fieldPathSchema = z
    .string()
    .min(1)
    .max(200)
    .refine((p) => parseWatchSelector(p) !== null && !p.includes('['), {
    message: 'path must be a simple dotted field path (no array indices)',
});
export const WatchQueryWhereSchema = z.object({
    /** Field on each item, e.g. "battery" or "status.mode". */
    path: fieldPathSchema,
    op: opSchema,
    value: z.union([z.number(), z.string(), z.boolean()]),
});
export const WatchQuerySchema = z
    .object({
    /**
     * Path to the array under the (selector-projected) tool output.
     * e.g. "drones", "data.units". Omit when the projection itself is the array.
     */
    items: z
        .string()
        .min(1)
        .max(200)
        .refine((p) => parseWatchSelector(p) !== null && !p.includes('*'), {
        message: 'items must be a dotted path to an array (no [*])',
    })
        .optional(),
    /**
     * Optional per-item predicate. Omit to keep every item (thin-list / project
     * fields only).
     */
    where: WatchQueryWhereSchema.optional(),
    /**
     * Fields to keep on each item (simple keys or dotted paths).
     * Empty/omit = keep the whole item.
     */
    select: z.array(fieldPathSchema).max(20).optional(),
    /**
     * changed (default) — hash-on-change of the result list.
     * nonempty — also require length > 0 (rising-edge by condition default).
     */
    when: z.enum(['changed', 'nonempty']).default('changed'),
})
    .refine((q) => q.where != null || (q.select != null && q.select.length > 0) || q.items != null, {
    message: 'query needs items, select, and/or where — otherwise use a plain selector',
});
/** Split a validated dotted path into keys. */
function pathKeys(path) {
    return path.split('.').filter(Boolean);
}
/**
 * Compile a declarative query into sandboxed map JS + optional condition.
 * User strings are embedded via JSON.stringify — never raw concatenation.
 */
export function compileWatchQuery(query) {
    const itemsKeys = query.items ? pathKeys(query.items) : null;
    const whereKeys = query.where ? pathKeys(query.where.path) : null;
    const selectKeys = (query.select ?? []).map(pathKeys);
    const when = query.when ?? 'changed';
    const code = [
        '/** Auto-compiled watch query — project list items (optional filter). */',
        'function getPath(obj, parts) {',
        '  var cur = obj;',
        '  for (var i = 0; i < parts.length; i++) {',
        '    if (cur == null || typeof cur !== "object") return undefined;',
        '    cur = cur[parts[i]];',
        '  }',
        '  return cur;',
        '}',
        'function pick(obj, paths) {',
        '  if (!paths || paths.length === 0) return obj;',
        '  var out = {};',
        '  for (var p = 0; p < paths.length; p++) {',
        '    var parts = paths[p];',
        '    var v = getPath(obj, parts);',
        '    if (v === undefined) continue;',
        '    if (parts.length === 1) { out[parts[0]] = v; }',
        '    else {',
        '      var leaf = parts[parts.length - 1];',
        '      out[leaf] = v;',
        '    }',
        '  }',
        '  return out;',
        '}',
        'function cmp(left, op, right) {',
        '  if (op === "eq") return left === right;',
        '  if (op === "neq") return left !== right;',
        '  if (typeof left !== "number" || typeof right !== "number") return false;',
        '  if (op === "gt") return left > right;',
        '  if (op === "gte") return left >= right;',
        '  if (op === "lt") return left < right;',
        '  if (op === "lte") return left <= right;',
        '  return false;',
        '}',
        `var itemsKeys = ${JSON.stringify(itemsKeys)};`,
        `var whereKeys = ${JSON.stringify(whereKeys)};`,
        `var selectPaths = ${JSON.stringify(selectKeys)};`,
        `var whereOp = ${JSON.stringify(query.where?.op ?? null)};`,
        `var whereVal = ${JSON.stringify(query.where ? query.where.value : null)};`,
        'var root = input.output;',
        // If the caller already selected the array (selector=drones) but also set
        // items="drones", getPath(array, ["drones"]) misses — fall back to root
        // when it is already an array.
        'var items = itemsKeys ? getPath(root, itemsKeys) : root;',
        'if (!Array.isArray(items) && Array.isArray(root)) items = root;',
        'if (!Array.isArray(items)) items = [];',
        'var out = [];',
        'for (var i = 0; i < items.length; i++) {',
        '  var it = items[i];',
        '  if (whereKeys) {',
        '    var v = getPath(it, whereKeys);',
        '    if (!cmp(v, whereOp, whereVal)) continue;',
        '  }',
        '  out.push(pick(it, selectPaths));',
        '}',
        'return out;',
    ].join('\n');
    if (when === 'nonempty') {
        return {
            code,
            condition: { op: 'gt', value: 0, risingEdge: true },
        };
    }
    return { code };
}
/**
 * Reverse a compileWatchQuery() body back into form fields.
 * Returns null when `code` is not an auto-compiled query map.
 */
export function parseCompiledWatchQuery(code) {
    if (!code || !code.includes('Auto-compiled watch query'))
        return null;
    const itemsM = /var itemsKeys = (\[[\s\S]*?\]|null);/.exec(code);
    const whereKeysM = /var whereKeys = (\[[\s\S]*?\]|null);/.exec(code);
    const selectM = /var selectPaths = (\[[\s\S]*?\]);/.exec(code);
    const opM = /var whereOp = ("(?:gt|gte|lt|lte|eq|neq)"|null);/.exec(code);
    const valM = /var whereVal = ([\s\S]*?);\n/.exec(code);
    if (!whereKeysM || !opM || !valM)
        return null;
    try {
        const itemsRaw = itemsM?.[1];
        const whereRaw = whereKeysM[1];
        const selectRaw = selectM?.[1];
        const opRawStr = opM[1];
        const valRaw = valM[1];
        if (whereRaw == null || opRawStr == null || valRaw == null)
            return null;
        const itemsKeys = itemsRaw != null ? JSON.parse(itemsRaw) : null;
        const whereKeys = JSON.parse(whereRaw);
        const selectPaths = selectRaw != null ? JSON.parse(selectRaw) : [];
        const opRaw = JSON.parse(opRawStr);
        const value = JSON.parse(valRaw);
        const where = whereKeys && whereKeys.length > 0 && opRaw != null && value !== null && value !== undefined
            ? { path: whereKeys.join('.'), op: opRaw, value }
            : undefined;
        // when=nonempty is not embedded in code; callers infer from condition.
        return {
            ...(itemsKeys && itemsKeys.length > 0 ? { items: itemsKeys.join('.') } : {}),
            ...(where ? { where } : {}),
            ...(selectPaths.length > 0 ? { select: selectPaths.map((parts) => parts.join('.')) } : {}),
            when: 'changed',
        };
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=watch-query.js.map